import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine } from 'recharts'
import { authenticatedFetch } from '../../../services/api/apiClient'
import { apiEndpoints } from '../../../consts'
import logger from '../../../utils/logger'
import { LoadingSpinner } from '../../atoms'
import { DrawerCard, DrawerBadge, DrawerEmptyState } from '../../DrawerComponents'
import styles from '../CallDrawerModern.module.scss'

const SENTIMENT_CONFIG = {
  Happy: { value: 6, color: '#4CAF50', emoji: '😊', label: 'Happy' },
  Satisfied: { value: 5, color: '#8BC34A', emoji: '🙂', label: 'Satisfied' },
  Calm: { value: 4, color: '#2196F3', emoji: '😌', label: 'Calm' },
  Neutral: { value: 3, color: '#9E9E9E', emoji: '😐', label: 'Neutral' },
  Anxious: { value: 2, color: '#FF9800', emoji: '😟', label: 'Anxious' },
  Frustrated: { value: 1, color: '#FF5722', emoji: '😤', label: 'Frustrated' },
  Angry: { value: 0, color: '#F44336', emoji: '😠', label: 'Angry' }
}

const formatTime = (seconds) => {
  if (!seconds && seconds !== 0) return '--:--'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const CustomDot = (props) => {
  const { cx, cy, payload } = props
  if (!payload?.sentimentColor) return null

  return (
    <circle
      cx={cx}
      cy={cy}
      r={6}
      fill={payload.sentimentColor}
      stroke="#fff"
      strokeWidth={2}
    />
  )
}

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    const config = SENTIMENT_CONFIG[data.sentiment] || {}

    return (
      <Box className={styles.chartTooltip}>
        <Typography style={{ fontWeight: 600, marginBottom: 4 }}>
          Segment {data.segment}
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <span style={{ fontSize: 16 }}>{config.emoji}</span>
          <span style={{ color: config.color, fontWeight: 500 }}>{data.sentiment}</span>
        </Box>
        {data.timeRange && (
          <Typography variant="caption" color="textSecondary">
            {data.timeRange}
          </Typography>
        )}
      </Box>
    )
  }
  return null
}

export const SentimentCard = ({ selectedRow, isGenerating: parentIsGenerating }) => {
  const [sentimentAnalysis, setSentimentAnalysis] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasChecked, setHasChecked] = useState(false)
  const [showSegmentDetails, setShowSegmentDetails] = useState(false)

  const isGeneratingFromParent = parentIsGenerating

  const getCallId = useCallback(() => {
    if (selectedRow?.id) return selectedRow.id
    if (selectedRow?.call_id) return selectedRow.call_id
    if (selectedRow?.[3]) return selectedRow[3]
    return null
  }, [selectedRow])

  const fetchSentimentAnalysis = useCallback(async () => {
    const callId = getCallId()
    if (!callId) return

    try {
      const response = await authenticatedFetch(apiEndpoints.sentimentByCall(callId), {
        method: 'GET'
      })

      if (response.ok) {
        const data = await response.json()
        if (data.data?.sentimentAnalysis) {
          setSentimentAnalysis(data.data.sentimentAnalysis)
        }
      }
    } catch (error) {
      logger.error('Error fetching sentiment analysis:', error)
    }
  }, [getCallId])

  /* eslint-disable react-hooks/set-state-in-effect -- intentional reset when selectedRow changes */
  useEffect(() => {
    setHasChecked(false)
    setSentimentAnalysis(null)
    setIsLoading(true)
  }, [selectedRow?.id, selectedRow?.call_id])
  /* eslint-enable react-hooks/set-state-in-effect */

  useEffect(() => {
    const checkData = async () => {
      if (!selectedRow || hasChecked) return

      setHasChecked(true)

      if (selectedRow.sentiment_analysis) {
        try {
          const parsed = typeof selectedRow.sentiment_analysis === 'string'
            ? JSON.parse(selectedRow.sentiment_analysis)
            : selectedRow.sentiment_analysis
          if (parsed?.segments) {
            setSentimentAnalysis(parsed)
            setIsLoading(false)
            return
          }
        } catch (e) {
        }
      }

      await fetchSentimentAnalysis()
      setIsLoading(false)
    }

    checkData()
  }, [selectedRow, hasChecked, fetchSentimentAnalysis])

  const segments = sentimentAnalysis?.segments || []
  const chartData = segments.map((segment, index) => ({
    segment: index + 1,
    sentimentValue: SENTIMENT_CONFIG[segment.sentiment]?.value || 3,
    sentimentColor: SENTIMENT_CONFIG[segment.sentiment]?.color || '#9E9E9E',
    sentiment: segment.sentiment,
    timeRange: `${formatTime(segment.start_time)} - ${formatTime(segment.end_time)}`,
    quote: segment.transcript_segment,
    confidence: segment.confidence
  }))

  const getOverallSentiment = () => {
    if (!chartData.length) return null
    const avgValue = chartData.reduce((sum, d) => sum + d.sentimentValue, 0) / chartData.length
    const sentiments = Object.entries(SENTIMENT_CONFIG)
    let closest = sentiments[0]
    sentiments.forEach(([name, config]) => {
      if (Math.abs(config.value - avgValue) < Math.abs(closest[1].value - avgValue)) {
        closest = [name, config]
      }
    })
    return { name: closest[0], ...closest[1] }
  }

  const overallSentiment = getOverallSentiment()

  const getTrend = () => {
    if (chartData.length < 2) return null
    const firstHalf = chartData.slice(0, Math.ceil(chartData.length / 2))
    const secondHalf = chartData.slice(Math.ceil(chartData.length / 2))
    const firstAvg = firstHalf.reduce((sum, d) => sum + d.sentimentValue, 0) / firstHalf.length
    const secondAvg = secondHalf.reduce((sum, d) => sum + d.sentimentValue, 0) / secondHalf.length

    if (secondAvg > firstAvg + 0.5) return { direction: 'up', label: 'Improving', icon: 'arrow-up', className: styles.sentimentTrendUp }
    if (secondAvg < firstAvg - 0.5) return { direction: 'down', label: 'Declining', icon: 'arrow-down', className: styles.sentimentTrendDown }
    return { direction: 'stable', label: 'Stable', icon: 'minus', className: styles.sentimentTrendStable }
  }

  const trend = getTrend()

  const showGenerating = isLoading || (isGeneratingFromParent && !sentimentAnalysis)

  const renderBadge = () => {
    if (showGenerating) {
      return (
        <DrawerBadge variant="info">
          <LoadingSpinner size={12} />
          <span>Generating...</span>
        </DrawerBadge>
      )
    }
    if (overallSentiment) {
      return (
        <Box className={styles.sentimentBadge} style={{ background: `${overallSentiment.color}20` }}>
          <span>{overallSentiment.emoji}</span>
          <span>{overallSentiment.name}</span>
        </Box>
      )
    }
    return null
  }

  return (
    <DrawerCard
      title="Sentiment Analysis"
      icon="chart-line"
      badge={renderBadge()}
      collapsible
      defaultExpanded={!!selectedRow?.sentiment_analysis}
      fullWidth
    >
      <Box>
        {showGenerating ? (
          <Box className={styles.loadingIndicator}>
            <LoadingSpinner size={20} message='Generating sentiment analysis...' />
          </Box>
        ) : sentimentAnalysis && chartData.length > 0 ? (
          <>
            {/* Summary Stats Row */}
            <Box className={styles.sentimentSummaryRow}>
              <Box className={styles.sentimentStat}>
                <Typography className={styles.sentimentStatLabel}>Overall</Typography>
                <Typography className={styles.sentimentStatValue}>
                  <span style={{ marginRight: 6 }}>{overallSentiment?.emoji}</span>
                  {overallSentiment?.name}
                </Typography>
              </Box>
              {trend && (
                <Box className={styles.sentimentStat}>
                  <Typography className={styles.sentimentStatLabel}>Trend</Typography>
                  <Typography className={`${styles.sentimentStatValue} ${trend.className}`}>
                    <FontAwesomeIcon icon={trend.icon} style={{ marginRight: 6 }} />
                    {trend.label}
                  </Typography>
                </Box>
              )}
              <Box className={styles.sentimentStat}>
                <Typography className={styles.sentimentStatLabel}>Segments</Typography>
                <Typography className={styles.sentimentStatValue}>
                  {chartData.length}
                </Typography>
              </Box>
            </Box>

            {/* Chart */}
            <Box className={styles.sentimentChartContainer}>
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                  <XAxis
                    dataKey="segment"
                    tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
                    axisLine={{ stroke: 'var(--color-border)' }}
                    tickLine={false}
                  />
                  <YAxis domain={[0, 6]} hide />
                  <ReferenceLine y={3} stroke="var(--color-border)" strokeDasharray="3 3" />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="sentimentValue"
                    stroke="#4E84A8"
                    strokeWidth={2}
                    dot={<CustomDot />}
                    activeDot={{ r: 8, fill: '#4E84A8', stroke: '#fff', strokeWidth: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>

            {/* Segment Details - Collapsible */}
            <Box className={styles.sentimentSegments}>
              <Box
                className={styles.segmentsTitleRow}
                onClick={() => setShowSegmentDetails(!showSegmentDetails)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setShowSegmentDetails(!showSegmentDetails)
                  }
                }}
              >
                <Typography className={styles.segmentsTitle}>
                  Segment Details ({chartData.length})
                </Typography>
                <FontAwesomeIcon
                  icon="chevron-down"
                  className={`${styles.segmentsChevron} ${showSegmentDetails ? styles.segmentsChevronOpen : ''}`}
                />
              </Box>

              {showSegmentDetails && (
                <Box className={styles.segmentsList}>
                  {chartData.map((segment, index) => {
                    const config = SENTIMENT_CONFIG[segment.sentiment] || {}
                    return (
                      <Box key={`${getCallId()}-${index}`} className={styles.segmentItem}>
                        <Box className={styles.segmentHeader}>
                          <Box className={styles.segmentNumber}>
                            {segment.segment}
                          </Box>
                          <Box
                            className={styles.segmentSentimentBadge}
                            style={{ backgroundColor: `${config.color}15`, color: config.color }}
                          >
                            <span>{config.emoji}</span>
                            <span>{segment.sentiment}</span>
                          </Box>
                        </Box>
                        {segment.quote && (
                          <Typography className={styles.segmentQuote}>
                            &ldquo;{segment.quote}&rdquo;
                          </Typography>
                        )}
                      </Box>
                    )
                  })}
                </Box>
              )}
            </Box>
          </>
        ) : hasChecked && !showGenerating ? (
          <DrawerEmptyState
            icon="chart-line"
            text="No sentiment data available"
            subtext="Sentiment analysis has not been generated for this call."
          />
        ) : null}
      </Box>
    </DrawerCard>
  )
}

SentimentCard.propTypes = {
  selectedRow: PropTypes.object,
  isGenerating: PropTypes.bool
}

export default SentimentCard
